"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { FitResult } from "@/lib/calculators/truck-loading-merged/types";
import { clampOnCommit } from "./results-totals";

export function UtilizationBar({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold text-foreground tabular-nums">
          {value.toFixed(1)}%
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full w-full rounded-full origin-left transition-transform duration-slow ease-out ${color}`}
          style={{ transform: `scaleX(${Math.min(1, Math.max(0, value / 100))})` }}
        />
      </div>
    </div>
  );
}

function ClampedNumberField({
  id,
  label,
  max,
  value,
  hint,
  onCommit,
}: {
  id: string;
  label: string;
  max: number;
  value: number;
  hint: string;
  onCommit: (value: number) => void;
}) {
  const [text, setText] = useState(String(value));
  const [previousValue, setPreviousValue] = useState(value);
  if (value !== previousValue) {
    setPreviousValue(value);
    setText(String(value));
  }
  return (
    <div>
      <Label htmlFor={id} size="sm">
        {label}
        <span className="ml-1 tabular-nums text-muted-foreground">max {max}</span>
      </Label>
      <Input
        id={id}
        type="number"
        min={0}
        max={max}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          const clamped = clampOnCommit(text, max);
          setText(String(clamped));
          onCommit(clamped);
        }}
        className="mt-1"
        variant="mono"
      />
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
  );
}

export function StackAdjustmentInputs({
  result,
  perFloor,
  stackLayers,
  singularLabel,
  onPerFloorChange,
  onStackLayersChange,
}: {
  result: FitResult;
  perFloor: number;
  stackLayers: number;
  singularLabel: string;
  onPerFloorChange: (value: number) => void;
  onStackLayersChange: (value: number) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <ClampedNumberField
        id="stacks"
        label="Per Floor"
        max={result.floorCount}
        value={perFloor}
        hint="Floor positions"
        onCommit={onPerFloorChange}
      />
      <ClampedNumberField
        id="per-stack"
        label="Stack Layers"
        max={result.stackLayers}
        value={stackLayers}
        hint={`${singularLabel}s high`}
        onCommit={onStackLayersChange}
      />
    </div>
  );
}
